import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { api } from '../lib/api-client';
import toast from 'react-hot-toast';
import { ThemeToggle, ThemeEditor, ThemeExportImport } from './theme';
import { IconButton } from './shared/StandardButtons';

export default function UserSettingsModal({ isOpen, onClose }) {
  const { user, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isUpdating, setIsUpdating] = useState(false);
  const [themeEditorOpen, setThemeEditorOpen] = useState(false);

  // Profile edit state
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatarImage, setAvatarImage] = useState(user?.avatar_image || '');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  if (!isOpen) return null;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be less than 2MB');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarImage(reader.result);
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setAvatarImage('');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    setIsUpdating(true);
    try {
      const updateData = {};

      // Only include fields that have values
      if (fullName && fullName.trim()) {
        updateData.full_name = fullName.trim();
      }
      if (email && email.trim()) {
        updateData.email = email.trim();
      }
      if (avatarImage) {
        updateData.avatar_image = avatarImage;
      }

      const updatedUser = await api.put('/api/v1/auth/profile', updateData);

      // Update user info in storage
      const storage = localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
      const storedUser = storage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        userData.full_name = updatedUser.full_name;
        userData.email = updatedUser.email;
        userData.avatar_image = updatedUser.avatar_image;
        storage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }

      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      // Error already shown by API interceptor
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    setIsUpdating(true);
    try {
      await api.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success('Password updated successfully!');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to update password:', error);
      // Error already shown by API interceptor
    } finally {
      setIsUpdating(false);
    }
  };

  // Get avatar display
  const getAvatarDisplay = () => {
    if (avatarImage) {
      return (
        <img
          src={avatarImage}
          alt={user?.username}
          className="w-24 h-24 rounded-full object-cover border-4 border-gray-700"
        />
      );
    } else if (user?.avatar_url) {
      return (
        <img
          src={user.avatar_url}
          alt={user?.username}
          className="w-24 h-24 rounded-full object-cover border-4 border-gray-700"
        />
      );
    } else {
      const initial = user?.username?.charAt(0).toUpperCase() || 'U';
      return (
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center font-bold text-3xl" style={{ color: 'var(--color-text-on-accent)' }}>
          {initial}
        </div>
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent" style={{ color: 'var(--color-text-on-accent)' }}>
                      User Settings
          </h2>
          <IconButton onClick={onClose} title="Close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </IconButton>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex space-x-1 border-b border-gray-700 flex-shrink-0">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-all duration-200 ${
              activeTab === 'profile'
                ? 'bg-gray-700 text-white'
                : ''
            }`}
            style={activeTab !== 'profile' ? { color: 'var(--color-text-muted)' } : {}}
            onMouseEnter={(e) => {
              if (activeTab !== 'profile') {
                e.currentTarget.style.color = 'var(--color-text-primary)';
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'profile') {
                e.currentTarget.style.color = 'var(--color-text-muted)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-all duration-200 ${
              activeTab === 'security'
                ? 'bg-gray-700 text-white'
                : ''
            }`}
            style={activeTab !== 'security' ? { color: 'var(--color-text-muted)' } : {}}
            onMouseEnter={(e) => {
              if (activeTab !== 'security') {
                e.currentTarget.style.color = 'var(--color-text-primary)';
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'security') {
                e.currentTarget.style.color = 'var(--color-text-muted)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            Security
          </button>
          <button
            onClick={() => setActiveTab('theme')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-all duration-200 ${
              activeTab === 'theme'
                ? 'bg-gray-700 text-white'
                : ''
            }`}
            style={activeTab !== 'theme' ? { color: 'var(--color-text-muted)' } : {}}
            onMouseEnter={(e) => {
              if (activeTab !== 'theme') {
                e.currentTarget.style.color = 'var(--color-text-primary)';
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'theme') {
                e.currentTarget.style.color = 'var(--color-text-muted)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            Theme
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {getAvatarDisplay()}
                </div>
                <div className="flex-1">
                  <label className="block">
                    <span className="sr-only">Choose avatar</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer cursor-pointer"
                      style={{ color: 'var(--color-text-on-accent)' }}
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    PNG, JPG, GIF up to 2MB
                  </p>
                  {avatarImage && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="mt-2 text-sm text-red-400 hover:text-red-300"
                    >
                      Remove image
                    </button>
                  )}
                </div>
              </div>

              {/* Profile Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={user?.username || ''}
                    disabled
                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-400 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
                    placeholder="Enter your full name"
                    disabled={isUpdating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
                    placeholder="Enter your email"
                    disabled={isUpdating}
                  />
                </div>
              </div>
            </form>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Change Password</h3>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
                      placeholder="Enter current password"
                      disabled={isUpdating}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
                      placeholder="Enter new password"
                      disabled={isUpdating}
                    />
                    <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters long</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
                      placeholder="Confirm new password"
                      disabled={isUpdating}
                    />
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'theme' && (
            <div className="space-y-6">
              {/* Theme Selection */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Appearance</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Customize how the application looks. Choose from built-in themes or create your own.
                </p>
                <ThemeToggle
                  showLabel={true}
                  onEditCustom={() => setThemeEditorOpen(true)}
                />
              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl text-base font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-4 focus:ring-gray-500/50 transition-all duration-200"
          >
            Close
          </button>
          {activeTab === 'profile' && (
            <button
              onClick={handleProfileUpdate}
              disabled={isUpdating}
              className="px-6 py-3 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-500/50 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              {isUpdating ? 'Updating...' : 'Save Changes'}
            </button>
          )}
          {activeTab === 'security' && (
            <button
              onClick={handlePasswordChange}
              disabled={isUpdating}
              className="px-6 py-3 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-500/50 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              {isUpdating ? 'Updating...' : 'Update Password'}
            </button>
          )}
        </div>
      </div>

      {/* Theme Editor Modal */}
      <ThemeEditor
        isOpen={themeEditorOpen}
        onClose={() => setThemeEditorOpen(false)}
      />
    </div>
  );
}
